import React, { useEffect, useRef, useState } from "react";
import SearchableDropdown from "./SearchableDropdown";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { FaDownload, FaMapMarkerAlt } from "react-icons/fa";
import { SERVER_URL } from "../config";
import { PlusIcon } from "@heroicons/react/24/outline";

/* ── shared input style (mirrors AdminAttendanceOverview) ─────────────── */
const inputStyle =
    "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors";

const readonlyInputStyle =
    "w-full bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-400 placeholder-slate-300 focus:outline-none cursor-default select-none";

/* ════════════════════════════════════════════════════════════════════════════
   SALARY SLIP VIEW MODAL
════════════════════════════════════════════════════════════════════════════ */
export const SalarySlipModal = ({ slipId, onClose }) => {
    const [data, setData] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const slipRef = useRef();

    const fetchSlip = async () => {
        try {
            const res = await axios.get(
                `${SERVER_URL}/Rahul/fetchSalarySlip.php?slip_id=${slipId}`
            );
            setData(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchSlip();
    }, [slipId]);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = "auto"; };
    }, []);

    const grossEarnings =
        data?.earnings?.reduce((sum, e) => sum + Number(e.amount || 0), 0) || 0;

    const totalDeductions =
        data?.deductions?.reduce((sum, d) => sum + Number(d.amount || 0), 0) || 0;

    const netPay = grossEarnings - totalDeductions;

    const downloadPDF = async () => {
        setIsDownloading(true);
        setTimeout(async () => {
            const element = slipRef.current;
            const canvas = await html2canvas(element, {
                scale: 1.2,
                useCORS: true,
                backgroundColor: "#ffffff",
                logging: false
            });
            const imgData = canvas.toDataURL("image/jpeg", 0.7);
            const pdf = new jsPDF({
                orientation: "p",
                unit: "mm",
                format: "a4",
                compress: true,
                putOnlyUsedFonts: true
            });
            const pageWidth = 210;
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight, undefined, "FAST");
            pdf.save(`${data.employeeName}_SalarySlip_${data.month}.pdf`);
            setIsDownloading(false);
        }, 200);
    };

    if (!data) return null;

    return (
        /* Backdrop */
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(8px)" }}
        >
            {/* Top accent line */}
            <div
                className="fixed top-0 left-0 right-0 h-0.5 z-50"
                style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
            />

            {/* Modal card */}
            <div
                className="relative flex flex-col rounded-xl border border-slate-200 bg-white"
                style={{
                    boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
                    width: "100%",
                    maxWidth: "860px",
                    maxHeight: "90vh",
                    padding: "0",
                    overflow: "hidden"
                }}
            >
                {/* Modal Header bar */}
                {!isDownloading && (
                    <div
                        className="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200"
                        style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}
                    >
                        <div className="flex items-center gap-2.5">
                            {/* Blue accent bar */}
                            <div
                                className="w-1 h-6 rounded-full"
                                style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
                            />
                            <h2 className="text-lg font-bold tracking-tight text-slate-800 m-0">
                                Salary Slip
                            </h2>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={downloadPDF}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-50 bg-white transition-colors"
                            >
                                <FaDownload className="text-sm text-emerald-600" />
                                Download PDF
                            </button>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                            >
                                ✕ Close
                            </button>
                        </div>
                    </div>
                )}

                {/* Scrollable slip area */}
                <div
                    className="flex-1 overflow-y-auto overflow-x-auto"
                    style={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(148,163,184,0.5) rgba(241,245,249,1)",
                        background: "#f0f4f8"
                    }}
                >
                    {/* The actual PDF-ready slip */}
                    <div
                        ref={slipRef}
                        className="bg-white overflow-hidden"
                        style={{
                            width: "794px",
                            minHeight: "1123px",
                            padding: "20px",
                            boxShadow: "none",
                            borderRadius: "0"
                        }}
                    >
                        {/* HEADER BAR */}
                        <div
                            className="text-white px-6 py-4 flex justify-between items-center"
                            style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
                        >
                            <div className="flex items-start gap-3">
                                <img src="/logo.png" alt="Logo" className="h-8 bg-white p-1 rounded" />
                                <div className="flex flex-col">
                                    <h2 className="font-semibold text-sm">
                                        <strong><em>SBE Fintech Software Pvt. Ltd.</em></strong>
                                    </h2>
                                    <p className="text-xs leading-tight flex items-start gap-2 mt-1">
                                        <FaMapMarkerAlt className="text-red-300 text-[12px] mt-[2px] flex-shrink-0" />
                                        <span className="italic opacity-90">
                                            203/C, Bepin Behari Ganguly St, Kolkata, West Bengal 700012.
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <div className="text-sm font-semibold opacity-90">
                                <strong><em>{data.month} {data.year}</em></strong>
                            </div>
                        </div>

                        {/* BODY */}
                        <div className="p-6 bg-slate-50">

                            {/* EMPLOYEE DETAILS GRID */}
                            <div className="grid grid-cols-2 gap-x-16 gap-y-4 text-sm">
                                <div className="space-y-3">
                                    {[
                                        ["Employee Name", data.employeeName],
                                        ["Department", data.department],
                                        ["Working Days", data.paidDays],
                                        ["Bank Name", data.bankName],
                                        ["IFSC Code", data.ifscCode],
                                        ["PAN No", data.panNo],
                                    ].map(([label, value]) => (
                                        <div key={label}>
                                            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-0.5">{label}</p>
                                            <p className="text-sm text-slate-800 font-semibold">{value || "—"}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-3">
                                    {[
                                        ["Employee ID", data.employeeId],
                                        ["Designation", data.designation],
                                        ["LOP Days", data.lopDays],
                                        ["Account No", data.accountNo],
                                        ["PF Account", data.pfAccountNo],
                                        ["Pay Date", data.payDate],
                                    ].map(([label, value]) => (
                                        <div key={label}>
                                            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-0.5">{label}</p>
                                            <p className="text-sm text-slate-800 font-semibold">{value || "—"}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* NET PAY BLOCK */}
                            <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50 px-5 py-3 flex items-center justify-end">
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Total Net Pay</p>
                                    <p className="text-2xl font-extrabold text-blue-700 mt-0.5">
                                        ₹{netPay.toLocaleString()}
                                    </p>
                                </div>
                                {/* <div className="flex gap-6 text-right">
                                    <div>
                                        <p className="text-xs text-slate-400 uppercase tracking-wider">Gross Earnings</p>
                                        <p className="text-base font-bold text-emerald-600">₹{grossEarnings.toLocaleString()}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 uppercase tracking-wider">Total Deductions</p>
                                        <p className="text-base font-bold text-rose-500">₹{totalDeductions.toLocaleString()}</p>
                                    </div>
                                </div> */}
                            </div>

                            {/* TABLE */}
                            <div className="mt-5 rounded-xl overflow-hidden border border-slate-200">
                                <table className="w-full text-sm">
                                    <thead style={{ background: "#f8fafc" }}>
                                        <tr>
                                            <th className="p-3 text-left border-b border-slate-200">
                                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Earnings</span>
                                            </th>
                                            <th className="p-3 text-right border-b border-slate-200">
                                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</span>
                                            </th>
                                            <th className="p-3 text-left border-b border-slate-200">
                                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Deductions</span>
                                            </th>
                                            <th className="p-3 text-right border-b border-slate-200">
                                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                        {Array.from({
                                            length: Math.max(
                                                data.earnings?.length || 0,
                                                data.deductions?.length || 0
                                            )
                                        }).map((_, i) => (
                                            <tr key={i} className="border-t border-slate-100 hover:bg-blue-50/40 transition-colors">
                                                <td className="p-3 text-slate-700 text-xs font-medium">{data.earnings?.[i]?.name || ""}</td>
                                                <td className="p-3 text-right text-slate-700 text-xs font-medium">
                                                    {data.earnings?.[i]
                                                        ? `₹${Number(data.earnings[i].amount).toLocaleString()}`
                                                        : ""}
                                                </td>
                                                <td className="p-3 text-slate-700 text-xs font-medium">{data.deductions?.[i]?.name || ""}</td>
                                                <td className="p-3 text-right text-slate-700 text-xs font-medium">
                                                    {data.deductions?.[i]
                                                        ? `₹${Number(data.deductions[i].amount).toLocaleString()}`
                                                        : ""}
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Totals row */}
                                        <tr className="border-t border-slate-200" style={{ background: "#f8fafc" }}>
                                            <td className="p-3 text-xs font-bold text-emerald-700 uppercase tracking-wide">Gross Earnings</td>
                                            <td className="p-3 text-right text-xs font-bold text-emerald-700">
                                                ₹{grossEarnings.toLocaleString()}
                                            </td>
                                            <td className="p-3 text-xs font-bold text-rose-600 uppercase tracking-wide">Total Deductions</td>
                                            <td className="p-3 text-right text-xs font-bold text-rose-600">
                                                ₹{totalDeductions.toLocaleString()}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* FOOTER */}
                            <div className="mt-8">
                                <p className="text-xs text-slate-400">
                                    This is a system generated salary slip and does not require signature.
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* end slipRef */}
                </div>
                {/* end scrollable area */}
            </div>
            {/* end modal card */}
        </div>
        /* end backdrop */
    );
};

/* ════════════════════════════════════════════════════════════════════════════
   GENERATE SALARY SLIP MODAL
════════════════════════════════════════════════════════════════════════════ */
export const GenerateSlipModal = ({ onClose, onSuccess }) => {

    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [dbBasicPay, setDbBasicPay] = useState(0); // NEW: raw monthly basic pay from DB
    const currentDate = new Date();
    // Previous month logic
    const prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const prevMonth = prevDate.toLocaleString("default", { month: "long" }); // e.g. "February"
    const prevYear = prevDate.getFullYear(); // handles Jan → Dec of previous year automatically

    const [form, setForm] = useState({
        employeeName: "", employeeId: "", department: "", designation: "",
        pf_acc_no: "", bank_name: "", account_no: "", ifsc_code: "", pan: "",
        month: prevMonth,    year: prevYear,
        basic: "", hra: "", da: "", ta: "", pf: "",
        paidDays: "", lopDays: ""
    });

    const [errorMessage, setErrorMessage] = useState("");

    const fetchEmployees = async () => {
        try {
            const res = await axios.get(
                `${SERVER_URL}/Rahul/insertSalarySlip.php?action=getEmployees`
            );
            const formatted = (res.data || []).map(emp => ({
                label: `${emp.employee_name} (${emp.employee_id})`,
                value: emp.employee_id,
                employee_id: emp.employee_id,
                employee_name: emp.employee_name
            }));
            setEmployees(formatted);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => { fetchEmployees(); }, []);

    const fetchEmployeeDetails = async (employeeId) => {
        try {
            const res = await axios.get(
                `${SERVER_URL}/Rahul/insertSalarySlip.php?action=getEmployeeDetails&employee_id=${employeeId}`
            );
            const emp = res.data.data;
            setDbBasicPay(Number(emp.basic_pay) || 0); // NEW: store DB basic pay
            setForm((prev) => ({
                ...prev,
                employeeName: emp.employee_name,
                employeeId: emp.employee_id,
                department: emp.department,
                designation: emp.designation,
                pf_acc_no: emp.pf_acc_no,
                bank_name: emp.bank_name,
                account_no: emp.bank_acc_no,
                ifsc_code: emp.ifsc_code,
                pan: emp.pan_no,
                basic: "", // NEW: reset until working days are entered
                paidDays: "" // NEW: reset so old days don't linger from previous employee
            }));
        } catch (err) {
            console.error(err);
        }
    };

    const handleEmployeeSelect = (employee) => {
        setSelectedEmployee(employee);
        fetchEmployeeDetails(employee.employee_id);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // NEW: auto-calculate Basic Salary whenever working days changes
        if (name === "paidDays") {
            const workingDays = Number(value) || 0;
            const calculatedBasic = dbBasicPay
                ? ((dbBasicPay / 30) * workingDays).toFixed(2)
                : "";
            setForm((prev) => ({ ...prev, paidDays: value, basic: calculatedBasic }));
            return;
        }

        setForm({ ...form, [name]: value });
    };

    const handleSubmit = async () => {
        try {
            const res = await axios.post(
                `${SERVER_URL}/Rahul/insertSalarySlip.php?action=insertSalary`,
                {
                    employee_id: form.employeeId || "",
                    employee_name: form.employeeName || "",
                    department: form.department || "",
                    designation: form.designation || "",
                    pf_acc_no: form.pf_acc_no || "",
                    bank_name: form.bank_name || "",
                    account_no: form.account_no || "",
                    ifsc_code: form.ifsc_code || "",
                    pan: form.pan || "",
                    month: form.month || "",
                    year: form.year || "",
                    basic: form.basic || "",
                    hra: form.hra || "",
                    da: form.da || "",
                    ta: form.ta || "",
                    pf: form.pf || "",
                    esi: form.esi || "",
                    pt: form.pt || "",
                    tds: form.tds || "",
                    paidDays: form.paidDays || "",
                    lopDays: form.lopDays || ""
                }
            );
            if (!res.data.status) {
                setErrorMessage(res.data.message);
                return;
            }
            setErrorMessage("");
            onSuccess();
            onClose();
        } catch (err) {
            console.error(err);
            setErrorMessage("Server error. Please try again.");
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(8px)" }}
        >
            {/* Top accent line */}
            <div
                className="fixed top-0 left-0 right-0 h-0.5 z-50"
                style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
            />

            <div
                className="w-full md:w-[600px] max-h-[90vh] overflow-y-auto rounded-xl border border-slate-200 bg-white"
                style={{
                    boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(148,163,184,0.5) rgba(241,245,249,1)"
                }}
            >
                {/* Modal Header */}
                <div
                    className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200"
                    style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}
                >
                    <div className="flex items-center gap-2.5">
                        <div
                            className="w-1 h-6 rounded-full"
                            style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
                        />
                        <h2 className="text-lg font-bold tracking-tight text-slate-800 m-0">
                            Generate Salary Slip
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                    >
                        ✕ Close
                    </button>
                </div>

                {/* Form body */}
                <div className="px-6 py-5" style={{ background: "#f0f4f8" }}>

                    {/* Section: Employee */}
                    <div className="mb-4">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Employee</p>
                        <div className="rounded-xl bg-white border border-slate-200 p-4"
                            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                            <SearchableDropdown
                                label="Select Employee"
                                options={employees}
                                value={form.employeeId}
                                placeholder="Select Employee"
                                onChange={(val) => {
                                    const emp = employees.find(e => e.value === val);
                                    if (emp) handleEmployeeSelect(emp);
                                }}
                            />
                        </div>
                    </div>

                    {/* Section: Auto-filled Details */}
                    <div className="mb-4">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Employee Details</p>
                        <div className="rounded-xl bg-white border border-slate-200 p-4"
                            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {[
                                    [form.employeeId, "Employee ID"],
                                    [form.department, "Department"],
                                    [form.designation, "Designation"],
                                    [form.pf_acc_no, "PF Account Number"],
                                    [form.bank_name, "Bank Name"],
                                    [form.account_no, "Account Number"],
                                    [form.ifsc_code, "IFSC Code"],
                                    [form.pan, "PAN Number"],
                                    [form.month, "Month"],
                                    [form.year, "Year"],
                                ].map(([value, placeholder]) => (
                                    <div key={placeholder} className="relative">
                                        <label className="block text-xs text-slate-400 font-medium mb-1">{placeholder}</label>
                                        <input
                                            value={value}
                                            placeholder={placeholder}
                                            className={readonlyInputStyle}
                                            readOnly
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Section: Attendance (moved above Earnings so working days exist before basic calc reads intuitively) */}
                    <div className="mb-4">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Attendance</p>
                        <div className="rounded-xl bg-white border border-slate-200 p-4"
                            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {[
                                    ["paidDays", "Paid Days"],
                                    ["lopDays", "LOP Days"],
                                ].map(([name, placeholder]) => (
                                    <div key={name}>
                                        <label className="block text-xs text-slate-500 font-medium mb-1">{placeholder}</label>
                                        <input
                                            name={name}
                                            placeholder={placeholder}
                                            className={inputStyle}
                                            value={form[name]}
                                            onChange={handleChange}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Section: Earnings */}
                    <div className="mb-4">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Earnings</p>
                        <div className="rounded-xl bg-white border border-slate-200 p-4"
                            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {[
                                    ["basic", "Basic Salary"],
                                    ["hra", "HRA"],
                                    ["da", "Duty Allowance (DA)"],
                                    ["ta", "Travel Allowance (TA)"],
                                ].map(([name, placeholder]) => (
                                    <div key={name}>
                                        <label className="block text-xs text-slate-500 font-medium mb-1">{placeholder}</label>
                                        <input
                                            name={name}
                                            placeholder={placeholder}
                                            className={name === "basic" ? readonlyInputStyle : inputStyle}
                                            value={name === "basic" ? form.basic : undefined}
                                            readOnly={name === "basic"}
                                            onChange={handleChange}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Section: Deductions */}
                    <div className="mb-4">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Deductions</p>
                        <div className="rounded-xl bg-white border border-slate-200 p-4"
                            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {[
                                    ["pf", "PF"],
                                    ["esi", "ESI"],
                                    ["pt", "Professional Tax"],
                                    ["tds", "TDS"],
                                ].map(([name, placeholder]) => (
                                    <div key={name}>
                                        <label className="block text-xs text-slate-500 font-medium mb-1">{placeholder}</label>
                                        <input
                                            name={name}
                                            placeholder={placeholder}
                                            className={inputStyle}
                                            onChange={handleChange}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Error message */}
                    {errorMessage && (
                        <p className="mb-4 text-sm font-semibold text-rose-500 bg-rose-50 border border-rose-200 rounded-lg px-4 py-2.5">
                            {errorMessage}
                        </p>
                    )}

                    {/* Footer buttons */}
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-5 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 bg-white transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white transition-all"
                            style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                        >
                            <PlusIcon className="h-4 w-4" />
                            Generate
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════════════
   EMPLOYEE SALARY INFO MODAL
════════════════════════════════════════════════════════════════════════════ */
export const SalaryInfoModal = ({ isOpen, onClose, employee, onViewSlip }) => {
    const [month, setMonth] = useState("");
    const [year] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [salaryData, setSalaryData] = useState(null);
    const [checked, setChecked] = useState(false);
    const [slipId, setSlipId] = useState(null);

    if (!isOpen) return null;

    const checkSalary = async () => {
        if (!month) return;
        try {
            setLoading(true);
            setChecked(true);
            const res = await axios.post(
                `${SERVER_URL}/Rahul/checkSalarySlip.php`,
                { employee_id: employee.employee_id, pay_period: month }
            );
            if (res.data?.status && res.data?.exists) {
                setSalaryData(res.data.data);
                setSlipId(Number(res.data.data.slip_id));
            } else {
                setSalaryData(null);
                setSlipId(null);
            }
        } catch (err) {
            console.error(err);
            setSalaryData(null);
            setSlipId(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(8px)" }}
        >
            {/* Top accent line */}
            <div
                className="fixed top-0 left-0 right-0 h-0.5 z-50"
                style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
            />

            {/* Backdrop click */}
            <div className="absolute inset-0" onClick={onClose} />

            {/* Modal */}
            <div
                className="relative w-full md:w-[440px] rounded-xl bg-white border border-slate-200"
                style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
            >
                {/* Header */}
                <div
                    className="flex items-start justify-between px-6 py-4 border-b border-slate-200"
                    style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}
                >
                    <div className="flex items-start gap-2.5">
                        <div
                            className="w-1 h-10 rounded-full mt-0.5 flex-shrink-0"
                            style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
                        />
                        <div>
                            <h2 className="text-base font-bold text-slate-800 m-0">
                                {employee.employee_name}
                            </h2>
                            <p className="text-xs text-slate-400 mt-0.5">
                                {employee.employee_id} · Salary Information
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5" style={{ background: "#f0f4f8" }}>
                    <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-3"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div>
                            <label className="block text-xs text-slate-500 font-medium mb-1">Select Month</label>
                            <input
                                type="month"
                                className={inputStyle}
                                value={month}
                                max={new Date().toISOString().slice(0, 7)}
                                onChange={(e) => {
                                    setMonth(e.target.value);
                                    setChecked(false);
                                    setSalaryData(null);
                                    setSlipId(null);
                                }}
                            />
                        </div>
                        <button
                            onClick={checkSalary}
                            disabled={!month || loading}
                            className="w-full py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40"
                            style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                        >
                            {loading ? "Checking…" : "Check Salary"}
                        </button>
                    </div>

                    {/* Result */}
                    <div className="mt-4 text-center">
                        {checked && !loading && !salaryData && (
                            <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3">
                                <p className="text-sm font-semibold text-rose-600">
                                    No salary slip found for selected month.
                                </p>
                            </div>
                        )}
                        {!loading && salaryData && slipId && (
                            <button
                                onClick={() => { onViewSlip(slipId); onClose(); }}
                                className="flex items-center justify-center gap-2 mx-auto px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all"
                            >
                                👁 View Salary Slip
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};